const events = require("./questionChangeEvents.js");

const WithValidation = require("~/types/QuestionValidationStatus.js").QuestionWithValidation;

/**
 * Gets an array of key-path objects.  A key path provides parameters for doing the 
 * following, for a given object:
 * 
 * 1. Picking a property on that object which is an array (ArrayProp)
 * 2. Identifying an element within that array by comparing a specified property (Key) 
 *    with a specified value (Val)
 * 
 * An array of key-path objects can be used to recursively find sub-elements in a 
 * tree-like structure, and then apply an update to the the bottom-most element.  
 * See withUpdatedQuestion below.
 */
function getQuestionKeyPath(questionIDPath) {
    return [
        { ArrayProp: "Pages", Key: "GUID", Val: questionIDPath[3] },
        { ArrayProp: "Sections", Key: "GUID", Val: questionIDPath[4] },
        { ArrayProp: "Questions", Key: "GUID", Val: questionIDPath[5] }
    ];
};

function withUpdatedQuestion(obj, keyPath, update) {
    // When we're out of key map entries, we've hit the bottom.
    if (!keyPath.length) {
        const updated = update(obj);

        const leafResult = Object.assign({}, obj, updated);

        return leafResult;
    }
    // Otherwise, we use the first element in the key map to
    // get another object, then recurse with a shortened key
    // map.  We'll hit the bottom eventually.
    else {
        const idMatch = keyPath[0];
        const ar = obj[idMatch.ArrayProp];

        const elIndex = ar.findIndex((x) => x[idMatch.Key] === idMatch.Val);
        const el = ar[elIndex];

        const updatedNode = withUpdatedQuestion(el, keyPath.slice(1), update);

        const nodeResult = Object.assign({}, obj, {
            [idMatch.ArrayProp]: [
                ...ar.slice(0, elIndex),
                updatedNode,
                ...ar.slice(elIndex + 1)
            ]
        });

        return nodeResult;
    }
}

const questionChangeHandlers = {
    // Use Name property to be sure that we always have it right
    // The function signature is always prev, event, return.
    [events.AnswersChanged.Name](prev, event) {
        const update = (q) => WithValidation(
            Object.assign({}, q, {
                AnswerSelections: event.AnswerSelections,
                AdditionalComment: event.AdditionalComment,
                Task: event.Task
            }));

        const keyPath = getQuestionKeyPath(event.QuestionIDPath);

        const result = Object.assign({}, prev, {
            CurrentSurvey: withUpdatedQuestion(prev.CurrentSurvey, keyPath, update)
        });

        return result;
    },
    [events.UpdateStarted.Name](prev, event) {
        const update = (q) => WithValidation(
            Object.assign({}, q, {
                Updating: true,
                UpdateError: null,
                AnswerSelections: event.AnswerSelections,
                AdditionalComment: event.AdditionalComment,
                Task: event.Task
            }));

        const keyPath = getQuestionKeyPath(event.QuestionIDPath);

        const result = Object.assign({}, prev, {
            CurrentSurvey: withUpdatedQuestion(prev.CurrentSurvey, keyPath, update)
        });

        return result;
    },
    [events.UpdateErrored.Name](prev, event) {        
        const update = (q) => WithValidation(
            Object.assign({}, q, {
                Updating: false,
                UpdateError: event.Error,
                AnswerSelections: event.AnswerSelections,
                AdditionalComment: event.AdditionalComment,
                Task: event.Task
            }));

        const keyPath = getQuestionKeyPath(event.QuestionIDPath);

        const result = Object.assign({}, prev, {
            CurrentSurvey: withUpdatedQuestion(prev.CurrentSurvey, keyPath, update)
        });

        return result;
    },
    [events.UpdateSucceeded.Name](prev, event) {        
        const update = (q) => WithValidation(
            Object.assign({}, q, {
                Updating: false,
                UpdateError: null,
                UpdatedAt: new Date(),
                AnswerSelections: event.AnswerSelections,
                AdditionalComment: event.AdditionalComment,
                Task: event.Task
            }));

        const keyPath = getQuestionKeyPath(event.QuestionIDPath);

        const result = Object.assign({}, prev, {
            CurrentSurvey: withUpdatedQuestion(prev.CurrentSurvey, keyPath, update)
        });

        return result;
    }
};

module.exports = questionChangeHandlers;


// WEBPACK FOOTER //
// ./src/events/questionChange/questionChangeHandlers.js